/**
 * Hours per bucket, stacked by project.
 *
 * Sits directly under the four headline stats because it answers the question
 * those stats raise: 16 hours where, and on which days?
 *
 * Each series takes its project's own colour, so a bar segment and the "By
 * project" row beneath are the same colour and the chart needs no legend
 * lookup. Ad-hoc time is always the last series in --muted3, so the stack
 * always sums to the Logged figure above it.
 */

import { useMemo } from "react";

import { hours, periodBounds, type Period } from "@/lib/timeRollup";
import type { Project, TimeEntry } from "@/lib/types";

/** Hours in a working day — what every bucket's target line is built from. */
const DAILY_TARGET = 6;

/** How wide one bar is in calendar terms. */
type Grain = "day" | "week" | "month";
const AD_HOC = "__adhoc";

interface Bucket {
  label: string;
  /** Seconds per project id, plus AD_HOC. */
  by: Map<string, number>;
  total: number;
  weekend: boolean;
}

export function HoursChart({ entries, projects, period, offset, mobile }: {
  entries: TimeEntry[];
  projects: Project[];
  period: Period;
  offset: number;
  mobile: boolean;
}) {
  const { buckets, series, target, grain } = useMemo(() => {
    const bounds = periodBounds(period, offset);

    /**
     * The bar width follows the period, not the calendar.
     *
     * Every period used to be drawn in daily columns, so a quarter came out as
     * ninety bars two pixels wide — past the edge of the card and unreadable
     * long before that, and a month at thirty was not much better. A bar has to
     * be wide enough to see the colours stacked inside it, which puts the
     * useful ceiling around fifteen: a week in days, a month or a quarter in
     * weeks, anything longer in months. A quarter in months would fit too, but
     * three bars is a table with extra steps — thirteen weeks show the shape.
     */
    const grain: Grain = period === "day" || period === "week" ? "day"
      : period === "month" || period === "quarter" ? "week"
      : "month";

    const ymd = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    /** Monday of that date's week — the app is Monday-based throughout. */
    const mondayOf = (d: Date) => {
      const m = new Date(d);
      m.setHours(0, 0, 0, 0);
      m.setDate(m.getDate() - ((m.getDay() + 6) % 7));
      return m;
    };
    const keyOf = (d: Date) =>
      grain === "day" ? ymd(d)
      : grain === "week" ? ymd(mondayOf(d))
      : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

    // Walk the period one bucket at a time rather than one day at a time.
    const starts: Date[] = [];
    if (period === "day") {
      starts.push(new Date(bounds.start));
    } else {
      const cursor = grain === "week" ? mondayOf(bounds.start) : new Date(bounds.start);
      if (grain === "month") cursor.setDate(1);
      cursor.setHours(0, 0, 0, 0);
      while (cursor <= bounds.end) {
        starts.push(new Date(cursor));
        if (grain === "day") cursor.setDate(cursor.getDate() + 1);
        else if (grain === "week") cursor.setDate(cursor.getDate() + 7);
        else cursor.setMonth(cursor.getMonth() + 1);
      }
    }

    const buckets: Bucket[] = starts.map((d) => ({
      label:
        grain === "month" ? d.toLocaleDateString(undefined, { month: "short" })
        : grain === "week" ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
        : period === "week" ? d.toLocaleDateString(undefined, { weekday: "short" })
        : d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }),
      by: new Map<string, number>(),
      total: 0,
      // Only meaningful for daily bars; a week or a month is not a weekend.
      weekend: grain === "day" && (d.getDay() === 0 || d.getDay() === 6),
    }));
    const index = new Map(starts.map((d, i) => [keyOf(d), i]));

    const seen = new Set<string>();
    for (const e of entries) {
      const start = new Date(e.start);
      const i = index.get(keyOf(start));
      if (i === undefined) continue;
      const key = e.project || AD_HOC;
      seen.add(key);
      const b = buckets[i];
      b.by.set(key, (b.by.get(key) ?? 0) + (e.durationSeconds || 0));
      b.total += e.durationSeconds || 0;
    }

    // Ad-hoc always last so the stack reads project work first.
    const series = [
      ...projects.filter((p) => seen.has(p.id)).map((p) => ({ key: p.id, name: p.name, color: p.color })),
      ...(seen.has(AD_HOC) ? [{ key: AD_HOC, name: "Not project-specific", color: "var(--muted3)" }] : []),
    ];

    /**
     * The target line scales with the bar.
     *
     * A 6-hour line under a bar holding a whole month would sit on the floor
     * and tell you nothing. Five working days to a week, twenty-two to a month.
     */
    const target =
      grain === "month" ? DAILY_TARGET * 22
      : grain === "week" ? DAILY_TARGET * 5
      : DAILY_TARGET;

    return { buckets, series, target, grain };
  }, [entries, projects, period, offset]);

  if (series.length === 0) return null;

  // The ceiling has to clear the target as well as the tallest bar, or a period
  // that never reaches the target pushes the line off the top and loses the
  // only useful reading on the chart.
  const tallest = Math.max(0, ...buckets.map((b) => b.total / 3600));
  const ceiling = Math.max(tallest, target, period === "day" ? 8 : 0) * 1.08;

  const H = mobile ? 96 : 132;
  const horizontal = period === "day";

  return (
    <div style={{ marginTop: 14 }}>
      {/* Say what a bar is. The same chart now means a day, a week or a month
          depending on the period, and a reader should not have to infer that
          from the tick labels. */}
      {!horizontal && (
        <div style={{ fontSize: 11, color: "var(--muted2)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Hours per {grain}, by project
        </div>
      )}
      {horizontal ? (
        <HorizontalBar bucket={buckets[0]} series={series} ceiling={ceiling} target={target} />
      ) : (
        <>
          <div style={{ position: "relative", height: H, display: "flex", gap: 6, alignItems: "flex-end" }}>
            <div style={{
              position: "absolute", left: 0, right: 0, bottom: `${(target / ceiling) * 100}%`,
              borderTop: "1px dashed var(--line2)", pointerEvents: "none",
            }} />
            {buckets.map((b, i) => (
              <div
                key={i}
                title={`${b.label}: ${hours(b.total)} h`}
                style={{
                  flex: 1, height: "100%", display: "flex", flexDirection: "column",
                  justifyContent: "flex-end",
                  // A Saturday with hours on it is worth seeing, so weekends are
                  // tinted rather than hidden.
                  background: b.weekend ? "var(--bg2)" : "transparent",
                  borderRadius: 3,
                }}
              >
                {series.map((s) => {
                  const secs = b.by.get(s.key) ?? 0;
                  if (!secs) return null;
                  return (
                    <div key={s.key} style={{
                      height: `${(secs / 3600 / ceiling) * 100}%`,
                      background: s.color, minHeight: 2,
                    }} />
                  );
                })}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            {buckets.map((b, i) => (
              // Day names ("Mon") get a readable size; week and month ticks
              // stay small so thirteen "Sep 29"s still fit on a phone.
              <span key={i} style={{
                flex: 1, textAlign: "center",
                ...(grain === "day" ? { fontSize: 12, fontWeight: 500 } : { fontSize: 9.5 }),
                color: b.weekend ? "var(--muted3)" : grain === "day" ? "var(--muted)" : "var(--muted2)",
              }}>{b.label}</span>
            ))}
          </div>
        </>
      )}

      {/* The legend carries THIS period's totals, not all-time ones. */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10 }}>
        {series.map((s) => {
          const secs = buckets.reduce((n, b) => n + (b.by.get(s.key) ?? 0), 0);
          return (
            <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--ink3)" }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: s.color, flex: "none" }} />
              {s.name}
              <span className="mono" style={{ color: "var(--muted2)" }}>{hours(secs)} h</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** Day: one horizontal bar on a fixed axis, with the target as a vertical mark. */
function HorizontalBar({ bucket, series, ceiling, target }: {
  bucket: Bucket; series: { key: string; name: string; color: string }[]; ceiling: number; target: number;
}) {
  return (
    <div style={{ position: "relative", height: 30, background: "var(--tint)", borderRadius: 6, overflow: "hidden", display: "flex" }}>
      {series.map((s) => {
        const secs = bucket.by.get(s.key) ?? 0;
        if (!secs) return null;
        return <div key={s.key} style={{ width: `${(secs / 3600 / ceiling) * 100}%`, background: s.color }} />;
      })}
      <div style={{
        position: "absolute", top: 0, bottom: 0, left: `${(target / ceiling) * 100}%`,
        borderLeft: "1px dashed var(--muted3)",
      }} />
    </div>
  );
}
